import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { useCart } from '../context/CartContext';
import { orderAPI, contentAPI } from '../services/api';
import { OrderFormData, DeliveryOption, CartItem } from '../types';
import './Checkout.css';

import { trackFacebookEvent } from '../utils/facebookPixel';
import { gtmEvents } from '../utils/gtmDataLayer';

const Checkout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { items, clearCart, updateQuantity, removeItem } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [directOrderLoaded, setDirectOrderLoaded] = useState(false);
  const [directOrderItems, setDirectOrderItems] = useState<CartItem[]>([]);
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<number | null>(null);

  const [formData, setFormData] = useState<OrderFormData>({
    customer_name: '',
    customer_phone: '',
    shipping_address: '',
    notes: '',
  });

  // PreOrder state for auto-save (persisted across page reloads via sessionStorage)
  const [preorderId, setPreorderId] = useState<number | null>(() => {
    try {
      const saved = sessionStorage.getItem('checkout_preorder_id');
      return saved ? parseInt(saved, 10) : null;
    } catch {
      return null;
    }
  });
  const [, setIsSavingPreorder] = useState(false);
  const preorderSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle direct order data from sessionStorage — uses local state, NOT CartContext
  // This ensures the cart badge is never affected by clicking "অর্ডার করুন"
  useEffect(() => {
    const directOrderData = sessionStorage.getItem('directOrder');
    if (directOrderData && !directOrderLoaded) {
      try {
        const orderData = JSON.parse(directOrderData);

        // Use the product data directly from sessionStorage (already fetched)
        if (orderData.product) {
          const quantity = parseInt(orderData.quantity, 10) || 1;
          setDirectOrderItems([{
            product: orderData.product,
            quantity,
            unit_price: orderData.unit_price ?? orderData.product?.final_price,
            selected_color: orderData.selected_color || '',
            selected_size: orderData.selected_size || '',
            selected_weight: orderData.selected_weight || '',
            selected_attributes: orderData.selected_attributes || {},
          }]);
          sessionStorage.removeItem('directOrder');
          setDirectOrderLoaded(true);
        }
      } catch (err) {
        console.error('Error handling direct order:', err);
        setDirectOrderLoaded(true);
      }
    }
  }, [directOrderLoaded]);

  // Computed: use direct order items if present, otherwise use cart items
  const isDirectOrder = directOrderItems.length > 0;
  const activeItems = isDirectOrder ? directOrderItems : items;

  // Fetch delivery options
  useEffect(() => {
    const fetchDeliveryOptions = async () => {
      try {
        const response = await contentAPI.getDeliveryOptions();
        // Handle paginated response (DRF returns {results: []} for viewsets)
        const data = Array.isArray(response) ? response : (response.results || []);
        setDeliveryOptions(data);
        // Auto-select first option
        if (data.length > 0) {
          setSelectedDeliveryOption(data[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch delivery options:', err);
        setDeliveryOptions([]); // Ensure it's always an array
      }
    };
    fetchDeliveryOptions();
  }, []);

  const selectedOption = Array.isArray(deliveryOptions) ? deliveryOptions.find(opt => opt.id === selectedDeliveryOption) : null;
  const shippingCost = selectedOption ? Number(selectedOption.cost) : 0;
  const activeTotal = activeItems.reduce((sum, item) => {
    const price = item.unit_price ?? item.product.final_price ?? item.product.price;
    return sum + price * item.quantity;
  }, 0);
  const finalTotal = activeTotal + shippingCost;

  // Track InitiateCheckout
  useEffect(() => {
    if (activeItems.length > 0) {
      trackFacebookEvent('InitiateCheckout', {
        content_ids: activeItems.map(item => item.product.id.toString()),
        content_type: 'product',
        value: activeTotal,
        currency: 'BDT',
        num_items: activeItems.reduce((acc, item) => acc + item.quantity, 0)
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItems.length]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Auto-save preorder with debounce (500ms after user stops typing)
  const savePreorder = useCallback(async () => {
    // Only save if we have items and at least some form data
    if (activeItems.length === 0) return;
    if (!formData.customer_name && !formData.customer_phone && !formData.shipping_address) return;

    setIsSavingPreorder(true);
    try {
      const preorderData = {
        customer_name: formData.customer_name || '',
        customer_phone: formData.customer_phone || '',
        shipping_address: formData.shipping_address || '',
        notes: formData.notes || '',
        delivery_option: selectedDeliveryOption,
        shipping_cost: shippingCost,
        items: activeItems.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          selected_color: item.selected_color || '',
          selected_size: item.selected_size || '',
          selected_weight: item.selected_weight || '',
          selected_attributes: item.selected_attributes || {},
        })),
      };

      if (preorderId) {
        // Update existing preorder
        await orderAPI.updatePreOrder(preorderId, preorderData);
      } else {
        // Create new preorder
        const newPreorder = await orderAPI.createPreOrder(preorderData);
        if (newPreorder && newPreorder.id) {
          setPreorderId(newPreorder.id);
          try {
            sessionStorage.setItem('checkout_preorder_id', String(newPreorder.id));
          } catch {}
        }
      }
    } catch (err) {
      console.error('Auto-save preorder failed:', err);
      // Silently fail - don't interrupt user
    } finally {
      setIsSavingPreorder(false);
    }
  }, [formData, activeItems, selectedDeliveryOption, shippingCost, preorderId]);

  // Debounced auto-save effect
  useEffect(() => {
    // Clear previous timeout
    if (preorderSaveTimeoutRef.current) {
      clearTimeout(preorderSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save (500ms delay)
    preorderSaveTimeoutRef.current = setTimeout(() => {
      savePreorder();
    }, 500);

    // Cleanup on unmount
    return () => {
      if (preorderSaveTimeoutRef.current) {
        clearTimeout(preorderSaveTimeoutRef.current);
      }
    };
  }, [formData, items, selectedDeliveryOption, savePreorder]);

  const validateForm = (): boolean => {
    if (!formData.customer_name.trim()) {
      setError(t('checkout.errors.nameRequired'));
      return false;
    }
    if (!formData.customer_phone.trim() || formData.customer_phone.length < 11) {
      setError(t('checkout.errors.phoneInvalid'));
      return false;
    }
    if (!formData.shipping_address.trim()) {
      setError(t('checkout.errors.addressRequired'));
      return false;
    }
    if (!selectedDeliveryOption) {
      setError(t('checkout.selectDelivery') || 'অনুগ্রহ করে ডেলিভারি অপশন নির্বাচন করুন');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    if (activeItems.length === 0) {
      setError(t('checkout.errors.emptyCart'));
      return;
    }

    setLoading(true);

    try {
      // Track Facebook AddPaymentInfo
      await trackFacebookEvent('AddPaymentInfo', {
        content_ids: activeItems.map(item => item.product.id.toString()),
        content_type: 'product',
        value: finalTotal,
        currency: 'BDT',
        payment_type: 'Cash On Delivery'
      });

      // Track GTM/GA4 add_payment_info
      gtmEvents.addPaymentInfo(
        activeItems.map(item => ({
          id: item.product.id,
          name: item.product.name_en,
          price: item.product.final_price,
          quantity: item.quantity,
        })),
        finalTotal,
        'Cash On Delivery'
      );

      const orderData = {
        ...formData,
        delivery_option: selectedDeliveryOption,
        shipping_cost: shippingCost,
        items: activeItems.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          selected_color: item.selected_color || '',
          selected_size: item.selected_size || '',
          selected_weight: item.selected_weight || '',
          selected_attributes: item.selected_attributes || {},
        })),
      };

      let order;
      if (preorderId) {
        // Confirm existing preorder
        order = await orderAPI.confirmPreOrder(preorderId);
      } else {
        // Create new order directly (fallback if preorder wasn't created)
        order = await orderAPI.createOrder(orderData);
      }
      // Clear preorder tracking ID from session storage
      try {
        sessionStorage.removeItem('checkout_preorder_id');
      } catch {}

      // Only clear the shared cart on a regular cart checkout.
      // For direct orders, the cart is untouched.
      if (!isDirectOrder) {
        clearCart();
      }
      navigate(`/order-confirmation/${order.order_number}`);
    } catch (err: any) {
      console.error('Order creation failed:', err);

      let errorMessage = t('checkout.errors.orderFailed');

      if (err.response?.data) {
        const errorData = err.response.data;

        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.non_field_errors) {
          errorMessage = Array.isArray(errorData.non_field_errors)
            ? errorData.non_field_errors.join(', ')
            : errorData.non_field_errors;
        } else {
          const fieldErrors = Object.entries(errorData)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('; ');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (activeItems.length === 0) {
    return (
      <div className="checkout-page empty-state">
        <div className="container">
          <div className="empty-cart-content">
            <div className="empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
            </div>
            <h2>{t('cart.empty')}</h2>
            <p>{t('cart.emptyMessage') || 'Your count appears to be empty. Start shopping now!'}</p>
            <Link to="/products" className="btn btn-primary start-shopping-btn">
              {t('cart.continueShopping')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container">


        <div className="checkout-layout">

          {/* Left Column: Form */}
          <div className="checkout-form-column">
            <div className="checkout-card form-wrapper">

              <form id="checkout-form" onSubmit={handleSubmit}>
                {error && (
                  <div className="error-alert">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}

                <div className="form-section customer-info-section">
                  <div className="form-section-header">
                    <h3 className="section-title">
                      <span className="step-number">1</span>
                      {t('checkout.customerInfo')}
                    </h3>
                  </div>

                  <div className="customer-fields">
                    <div className="customer-form-grid">
                      <div className="customer-form-row">
                        <div className="form-group">
                          <label htmlFor="customer_name">
                            {t('checkout.name')} <span className="required">*</span>
                          </label>
                          <input
                            type="text"
                            id="customer_name"
                            name="customer_name"
                            value={formData.customer_name}
                            onChange={handleInputChange}
                            placeholder={t('checkout.namePlaceholder') || "Enter your full name"}
                            required
                            className="checkout-input"
                            autoComplete="name"
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="customer_phone">
                            {t('checkout.phone')} <span className="required">*</span>
                          </label>
                          <input
                            type="tel"
                            id="customer_phone"
                            name="customer_phone"
                            value={formData.customer_phone}
                            onChange={handleInputChange}
                            placeholder="01XXXXXXXXX"
                            required
                            className="checkout-input"
                            autoComplete="tel"
                            inputMode="numeric"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="shipping_address">
                          {t('checkout.address')} <span className="required">*</span>
                        </label>
                        <textarea
                          id="shipping_address"
                          name="shipping_address"
                          value={formData.shipping_address}
                          onChange={handleInputChange}
                          rows={3}
                          placeholder={t('checkout.addressPlaceholder') || "Enter your complete delivery address"}
                          required
                          className="checkout-input"
                          autoComplete="street-address"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="order_notes">{t('checkout.orderNotes')}</label>
                        <textarea
                          id="order_notes"
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          rows={2}
                          placeholder={t('checkout.notesPlaceholder') || "Any special instructions for delivery (optional)"}
                          className="checkout-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-section checkout-options-section">
                  <div className="form-section-header checkout-options-header">
                    <h3 className="section-title">
                      <span className="step-number">2</span>
                      {t('checkout.paymentMethod')}
                    </h3>
                  </div>
                  <div className="checkout-options-body">
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label className="delivery-section-label">
                        <span className="delivery-icon">🚚</span>
                        {t('checkout.selectDeliveryCharge')} <span className="required">*</span>
                      </label>
                      <div className="delivery-options">
                        {deliveryOptions.map(option => (
                          <label key={option.id} className={`delivery-option ${selectedDeliveryOption === option.id ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name="delivery_option"
                              value={option.id}
                              checked={selectedDeliveryOption === option.id}
                              onChange={() => setSelectedDeliveryOption(option.id)}
                              required
                            />
                            <div className="delivery-option-content">
                              {option.icon && <span className="option-icon">{option.icon}</span>}
                              <span className="option-name">
                                {i18n.language === 'bn' ? option.name_bn : option.name_en}
                              </span>
                              <span className="option-charge">{Number(option.cost).toFixed(2)} {t('checkout.taka')}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Payment Method */}
                    <div className="form-group full-width">
                      <div className="payment-method-box">
                        <div className="payment-header">
                          <div className="radio-circle selected"></div>
                          <span className="payment-title">{t('checkout.cashOnDelivery')}</span>
                        </div>
                        <p className="payment-desc">{t('checkout.paymentDesc')}</p>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Right Column: Order Summary */}
          <div className="checkout-summary-column">
            <div className="checkout-card summary-card sticky-card">
              <h3>{t('cart.orderSummary')}</h3>

              <div className="summary-products">
                {activeItems.map((item) => (
                  <div key={item.product.id} className="summary-product-item">
                    <div className="product-thumb">
                      {item.product.image || item.product.primary_image ? (
                        <img
                          src={item.product.image || item.product.primary_image}
                          alt={item.product.name}
                        />
                      ) : (
                        <div className="placeholder-thumb">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="product-info">
                      <h4 className="product-name">{item.product.name}</h4>
                      {((item.selected_color || item.selected_size || item.selected_weight) || (item.selected_attributes && Object.keys(item.selected_attributes).length > 0)) && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', margin: '3px 0 4px' }}>
                          {item.selected_color && (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              padding: '1px 7px', borderRadius: '12px', fontSize: '11px',
                              background: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB',
                            }}>
                              <span style={{
                                width: '8px', height: '8px', borderRadius: '50%',
                                background: item.selected_color, border: '1px solid #ccc',
                                flexShrink: 0,
                              }} />
                              {item.selected_color}
                            </span>
                          )}
                          {item.selected_size && (
                            <span style={{
                              padding: '1px 7px', borderRadius: '12px', fontSize: '11px',
                              background: '#EEF2FF', color: '#4F46E5', border: '1px solid #C7D2FE',
                            }}>
                              {item.selected_size}
                            </span>
                          )}
                          {item.selected_weight && (
                            <span style={{
                              padding: '1px 7px', borderRadius: '12px', fontSize: '11px',
                              background: '#ECFDF5', color: '#059669', border: '1px solid #A7F3D0',
                            }}>
                              {item.selected_weight}
                            </span>
                          )}
                          {item.selected_attributes && Object.entries(item.selected_attributes).map(([attrKey, attrVal]) => {
                            if (['color', 'size', 'weight', 'রং', 'সাইজ', 'ওজন'].includes(attrKey.toLowerCase())) return null;
                            return (
                              <span key={attrKey} style={{
                                padding: '1px 7px', borderRadius: '12px', fontSize: '11px',
                                background: '#FAF5FF', color: '#7E22CE', border: '1px solid #E9D5FF',
                                fontWeight: 600
                              }}>
                                {attrKey}: {attrVal}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <p className="product-price">
                        ৳{(item.unit_price ?? item.product.final_price ?? item.product.price).toFixed(2)} × {item.quantity}
                      </p>
                      <div className="quantity-controls">
                        <button
                          className="qty-btn"
                          onClick={() => {
                            if (item.quantity > 1) {
                              if (isDirectOrder) {
                                setDirectOrderItems(prev => prev.map(i =>
                                  i.product.id === item.product.id ? { ...i, quantity: i.quantity - 1 } : i
                                ));
                              } else {
                                updateQuantity(item.product.id, item.quantity - 1);
                              }
                            }
                          }}
                          disabled={item.quantity <= 1}
                        >
                          -
                        </button>
                        <span className="qty-display">{item.quantity}</span>
                        <button
                          className="qty-btn"
                          onClick={() => {
                            if (item.quantity < item.product.stock) {
                              if (isDirectOrder) {
                                setDirectOrderItems(prev => prev.map(i =>
                                  i.product.id === item.product.id ? { ...i, quantity: i.quantity + 1 } : i
                                ));
                              } else {
                                updateQuantity(item.product.id, item.quantity + 1);
                              }
                            }
                          }}
                          disabled={item.quantity >= item.product.stock}
                        >
                          +
                        </button>
                        <button
                          className="remove-item-btn"
                          onClick={() => {
                            if (isDirectOrder) {
                              setDirectOrderItems(prev => prev.filter(i => i.product.id !== item.product.id));
                            } else {
                              removeItem(item.product.id);
                            }
                          }}
                          title="Remove item"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                    <div className="product-total">
                      <span className="total-label">{t('cart.total')}:</span>
                      <span className="total-price">৳{(item.product.final_price * item.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="summary-breakdown">
                <div className="breakdown-row">
                  <span>{t('cart.subtotal')}</span>
                  <span>৳{activeTotal.toFixed(2)}</span>
                </div>
                <div className="breakdown-row shipping-row">
                  <span>{t('cart.shipping')}</span>
                  <span>৳{shippingCost.toFixed(2)}</span>
                </div>
                <div className="breakdown-divider"></div>
                <div className="breakdown-row total-row">
                  <span>{t('cart.total')}</span>
                  <span className="total-amount">৳{finalTotal.toFixed(2)}</span>
                </div>
              </div>

              <button
                type="submit"
                form="checkout-form"
                className="place-order-btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  <>
                    <span>{t('checkout.placeOrder')}</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>

              <div className="security-badges">
                <div className="badge-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  <span>{t('checkout.secureCheckout')}</span>
                </div>
                <div className="badge-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span>{t('checkout.trackedDelivery')}</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
